import React from 'react';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import type { StatisticsSummaryView } from '../../api/generated/models/StatisticsSummaryView';
import { getDisplayCurrency, getAmountForCurrency, formatMoney } from '../../utils/statisticsHelpers';

const CHART_COLORS = ['#2563EB', '#059669', '#D97706', '#DC2626', '#7C3AED', '#0891B2', '#65A30D', '#4F46E5'];

interface SalesByPaymentMethodsWidgetProps {
  data: StatisticsSummaryView | null;
}

export const SalesByPaymentMethodsWidget: React.FC<SalesByPaymentMethodsWidgetProps> = ({ data }) => {
  const { t } = useTranslation();
  const currency = getDisplayCurrency(data);

  const chartData = React.useMemo(() => {
    const items = data?.salesByPaymentMethod ?? [];
    return items
      .map((item: any) => ({
        name: item.paymentMethodName ?? '',
        value: getAmountForCurrency(item.amountByCurrency, currency),
      }))
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value)
      .map((item, i) => ({
        ...item,
        color: CHART_COLORS[i % CHART_COLORS.length],
      }));
  }, [data, currency]);

  return (
    <div className="stat-card-half">
      <div className="stat-card-header">
        <h3 className="stat-card-title">{t('dashboard_stats_sales_by_payment')}</h3>
      </div>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="55%"
              outerRadius="80%"
              paddingAngle={2}
              label={false}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value: number) => formatMoney(value, currency)} />
            <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="payment-legend">
        {chartData.map((d) => (
          <li key={d.name}>
            <span className={`payment-legend-color payment-legend-color--${CHART_COLORS.indexOf(d.color)}`} />
            <span>{d.name}</span>
            <span>{formatMoney(d.value, currency)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};
